import { Switch as AriaSwitch } from 'react-aria-components';
import type { SwitchProps as AriaSwitchProps } from 'react-aria-components';

import { type ReactNode } from 'react';

import { css } from '@allenai/varnish-panda-runtime/css';

import { cx } from '@/utils/cx';

import { groupFocusRing } from '../shared/focusRing.styles';
import switchRecipe, { SwitchRecipeProps } from '@/components/switch/switch.styles';
import { FieldError } from '../field/fieldError/FieldError';
import { Description } from '../field/description/Description';
import { Label } from '@/components/field/label/Label';

/**
 *  There is no native HTML element with switch styling. <input type="checkbox"> is the closest
 *  semantically, but isn't styled or exposed to assistive technology as a switch. Switch helps
 *  achieve accessible switches that can be styled as needed.
 *
 *  See: https://react-spectrum.adobe.com/react-aria/Switch.html
 */

type SwitchProps = {
    children?: ReactNode;
    label?: string;
    description?: string;
    errorMessage?: string;
    className?: string;
    trackClassName?: string;
    thumbClassName?: string;
    errorClassName?: string;
    labelClassName?: string;
    descriptionClassName?: string;
    contentContainerClassName?: string;
} & AriaSwitchProps &
    SwitchRecipeProps;

const Switch = ({
    children,
    label,
    description,
    errorMessage,
    className,
    trackClassName,
    thumbClassName,
    errorClassName,
    labelClassName,
    descriptionClassName,
    contentContainerClassName,
    ...rest
}: SwitchProps) => {
    const [variantProps, localProps] = switchRecipe.splitVariantProps(rest);
    const recipeClassNames = switchRecipe(variantProps);
    const { size } = rest;

    return (
        <AriaSwitch className={cx('group', recipeClassNames.root, className)} {...localProps}>
            <Label size={size} className={cx(recipeClassNames.label, labelClassName)}>
                {label}
            </Label>
            <div className={cx(recipeClassNames.contentContainer, contentContainerClassName)}>
                <div className={cx(css(groupFocusRing), recipeClassNames.track, trackClassName)}>
                    <div className={cx(recipeClassNames.thumb, thumbClassName)} />
                </div>
                {children}
            </div>
            <Description
                size={size}
                className={cx(recipeClassNames.description, descriptionClassName)}>
                {description}
            </Description>
            <FieldError size={size} className={cx(recipeClassNames.error, errorClassName)}>
                {errorMessage}
            </FieldError>
        </AriaSwitch>
    );
};

export { Switch, Switch as default };
export type { SwitchProps };
